/**
 * SEO Helper - Manage meta tags and structured data
 */

interface MetaTags {
  title: string;
  description: string;
  image?: string;
  url?: string;
  type?: 'website' | 'article';
  author?: string;
  published?: string;
  modified?: string;
}

/**
 * Update document meta tags
 */
export const updateMetaTags = (meta: MetaTags) => {
  // Title
  document.title = meta.title;
  
  // OG Tags
  updateMetaTag('og:title', meta.title);
  updateMetaTag('og:description', meta.description);
  updateMetaTag('og:type', meta.type || 'website');
  if (meta.image) updateMetaTag('og:image', meta.image);
  if (meta.url) updateMetaTag('og:url', meta.url);
  
  // Twitter Tags
  updateMetaTag('twitter:card', 'summary_large_image');
  updateMetaTag('twitter:title', meta.title);
  updateMetaTag('twitter:description', meta.description);
  if (meta.image) updateMetaTag('twitter:image', meta.image);
  
  // Standard Meta Tags
  updateMetaTag('description', meta.description);
  if (meta.author) updateMetaTag('author', meta.author);
  
  // Article specific
  if (meta.type === 'article') {
    if (meta.published) updateMetaTag('article:published_time', meta.published);
    if (meta.modified) updateMetaTag('article:modified_time', meta.modified);
  }
};

/**
 * Update or create a meta tag
 */
const updateMetaTag = (name: string, content: string) => {
  let element = document.querySelector(`meta[name="${name}"], meta[property="${name}"]`);
  
  if (!element) {
    element = document.createElement('meta');
    if (name.startsWith('og:') || name.startsWith('article:')) {
      element.setAttribute('property', name);
    } else {
      element.setAttribute('name', name);
    }
    document.head.appendChild(element);
  }
  
  element.setAttribute('content', content);
};

/**
 * Add canonical URL
 */
export const setCanonicalUrl = (url: string) => {
  let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
  
  if (!canonical) {
    canonical = document.createElement('link');
    canonical.rel = 'canonical';
    document.head.appendChild(canonical);
  }
  
  canonical.href = url;
};

/**
 * Add structured data (JSON-LD)
 */
export const addStructuredData = (data: any) => {
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.innerHTML = JSON.stringify(data);
  document.head.appendChild(script);
  
  return () => script.remove();
};

/**
 * Generate article structured data
 */
export const generateArticleStructuredData = (article: {
  headline: string;
  description: string;
  image: string;
  datePublished: string;
  dateModified: string;
  author: string;
  url: string;
}) => {
  return {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: article.headline,
    description: article.description,
    image: article.image,
    datePublished: article.datePublished,
    dateModified: article.dateModified,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': article.url,
    },
  };
};

/**
 * Generate organization structured data
 */
export const generateOrganizationStructuredData = () => {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Metapic',
    url: 'https://metapic.ai',
    logo: 'https://metapic.ai/logo.png',
    sameAs: [
      'https://twitter.com/metapic',
      'https://instagram.com/metapic',
    ],
  };
};
